/**
 * Widget ⋯ → "Add to a dashboard…" (screenshots/89 export menu; `addWidgetTo` in dashboards.ts): pick one of your
 * dashboards; the widget (with its chart settings) is appended at the first free spot and saved with the dashboard's
 * version (a 409 is retried once on the fresh copy). A toast confirms it, with Open.
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { LayoutGrid } from "lucide-react";
import { navigate } from "../app/router";
import { toastStore } from "../stores/toastStore";
import type { LayoutItem } from "../overview/types";
import { addWidgetTo, type Dashboard } from "./dashboards";
import { errorText } from "../lib/apiError";
import { analyticsStore, useAnalytics } from "./analyticsStore";

export async function addToDashboard(id: string, item: LayoutItem): Promise<Dashboard> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const cur = await fetch(`/api/dashboards/${encodeURIComponent(id)}`, { credentials: "include" }).then((r) => (r.ok ? r.json() as Promise<Dashboard> : Promise.reject(new Error(String(r.status)))));
    const newId = `${item.type}-${Date.now().toString(36)}`;
    const next = addWidgetTo(cur, { type: item.type, w: item.w, h: item.h, config: structuredClone(item.config) as Record<string, unknown> }, newId);
    const r = await fetch(`/api/dashboards/${encodeURIComponent(id)}`, { method: "PUT", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ baseVersion: cur.version, items: next.layout, touched: [newId] }) });
    if (r.status === 409) continue;
    if (!r.ok) throw new Error(errorText(await r.json().catch(() => ({}))) || String(r.status));
    const saved = ((await r.json()) as { dashboard: Dashboard }).dashboard;
    analyticsStore.getState().upsert(saved);
    return saved;
  }
  throw new Error("conflict");
}

export function AddToDashboardMenu({ item, anchor, onClose }: { item: LayoutItem; anchor: HTMLElement | null; onClose: () => void }) {
  const titles = useAnalytics((s) => s.titles);
  const here = typeof location === "undefined" ? "" : location.pathname.split("/")[3];
  const list = titles.filter((t) => t.id !== here);
  const ref = useRef<HTMLUListElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const away = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    addEventListener("pointerdown", away, true);
    return () => removeEventListener("pointerdown", away, true);
  }, [onClose]);
  const pick = (id: string, title: string) => {
    onClose();
    void addToDashboard(id, item).then(() => toastStore.getState().show({ message: `Added to “${title}”`, actionLabel: "Open", onAction: () => navigate(`/dashboard/analytics/${id}`) }))
      .catch((e: Error) => toastStore.getState().show({ message: e.message === "403" ? "You can view this dashboard but not change it." : "Couldn’t add the widget. Try again." }));
  };
  const r = anchor?.getBoundingClientRect();
  const style: React.CSSProperties = r ? { position: "fixed", right: Math.max(8, innerWidth - r.right), top: Math.min(r.bottom + 4, innerHeight - 260) } : {};
  return createPortal(
    <ul ref={ref} className="widget-menu ov-menu" role="menu" aria-label="Add to a dashboard" style={style} onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        const btns = [...(ref.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
        const at = btns.indexOf(document.activeElement as HTMLButtonElement);
        if (e.key === "Escape") { e.stopPropagation(); onClose(); }
        else if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); btns[(at + (e.key === "ArrowDown" ? 1 : -1) + btns.length) % btns.length]?.focus(); }
      }}>
      {list.map((t) => (
        <li key={t.id} role="none"><button role="menuitem" onClick={() => pick(t.id, t.title)}><LayoutGrid size={15} aria-hidden />{t.title}</button></li>
      ))}
    </ul>, document.body,
  );
}
